/**
 * Screening's program fit, sized against the client's versioned Credit Box
 * (UW-08, MOD-18).
 *
 * The sizer used to test every deal against five product boxes hardcoded in
 * the page — "Senior $15–100M ≤75%", "Large Loan", "B-Note", "Mezz ≤85%",
 * "Ground Lease ≤40%" — which are not the client's programs, and priced debt
 * service at an invented 6.5%. The Credit Box (GET
 * /golden-deal-reconciliation/credit-box, the same fingerprinted document the
 * Credit Box page shows) defines Program 1 (A Loans: ≤80% LTV, $20–100MM, 8%
 * min stabilized debt yield, all asset classes) and Program 2 (B Loans: ≤75%,
 * $20–75MM, 1.0x amortizing DSCR, multifamily), with a 7% minimum coupon and a
 * 25-year sizing amortization. This sizes a loan under each program from
 * those rules only. It is an indicative sizer: the recorded screen of a deal
 * is the server's, shown on the deal.
 */
import type { CreditBoxResponse } from '@/types'

export type ProgramFitStatus = 'In Box' | 'Out of Box' | 'Needs Input'

export interface ProgramFit {
  programId: string
  name: string
  status: ProgramFitStatus
  /** The largest loan the program allows at these inputs, in dollars (0 when out). */
  sizedLoan: number
  maxLtv: number
  reason: string
}

type Rules = CreditBoxResponse['rules']

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const pct = (v: number, d = 1) => `${(v * 100).toFixed(d)}%`

function normalizeClass(value: string): string {
  return value.toUpperCase().replace(/[^A-Z]/g, '')
}

/** Annual debt service per dollar of loan on a monthly-pay amortizing schedule. */
export function annualDebtConstant(coupon: number, amortYears: number): number {
  const r = coupon / 12
  const n = amortYears * 12
  if (r <= 0 || n <= 0) return 0
  return (12 * r) / (1 - Math.pow(1 + r, -n))
}

export function sizingConventions(rules: Rules): { coupon: number | null; amortYears: number | null } {
  const c = rules.conventions ?? {}
  const coupon = typeof c.minimum_coupon === 'number' ? c.minimum_coupon : null
  const amortYears = typeof c.amortization_years_for_sizing === 'number' ? c.amortization_years_for_sizing : null
  return { coupon, amortYears }
}

export function programFits(
  input: { value: number; noi: number; assetType: string },
  rules: Rules,
): ProgramFit[] {
  const { coupon, amortYears } = sizingConventions(rules)
  const constant = coupon != null && amortYears != null ? annualDebtConstant(coupon, amortYears) : null

  return Object.entries(rules.programs).map(([programId, p]) => {
    const base = { programId, name: p.name, maxLtv: p.max_ltv }
    const classes = p.asset_classes === 'ALL' ? null : (Array.isArray(p.asset_classes) ? p.asset_classes : [p.asset_classes])
    if (classes && !input.assetType.trim()) {
      // No asset class chosen yet: the program cannot be tested, it has not
      // been failed. An empty sizer must not open on an "Out of Box" verdict.
      return {
        ...base,
        status: 'Needs Input' as const,
        sizedLoan: 0,
        reason: `Select an asset type (${classes.join(', ').toLowerCase()} only)`,
      }
    }
    if (classes && !classes.map(normalizeClass).includes(normalizeClass(input.assetType))) {
      return {
        ...base,
        status: 'Out of Box' as const,
        sizedLoan: 0,
        reason: `${input.assetType} is not in this program (${classes.join(', ').toLowerCase()} only)`,
      }
    }
    if (!(input.value > 0)) {
      return { ...base, status: 'Needs Input' as const, sizedLoan: 0, reason: 'Enter a property value to size' }
    }

    let loan = input.value * p.max_ltv
    let limit = `${pct(p.max_ltv, 0)} LTV`
    const needsNoi = p.min_debt_yield_stabilized != null || p.min_dscr_amortizing != null
    if (needsNoi && !(input.noi > 0)) {
      return {
        ...base,
        status: 'Needs Input' as const,
        sizedLoan: Math.min(loan, p.loan_size_max),
        reason: `NOI needed for the ${p.min_debt_yield_stabilized != null ? 'debt-yield' : 'DSCR'} test; ${pct(p.max_ltv, 0)} LTV alone gives ${usd0.format(Math.min(loan, p.loan_size_max))}`,
      }
    }
    if (p.min_debt_yield_stabilized != null) {
      const cap = input.noi / p.min_debt_yield_stabilized
      if (cap < loan) {
        loan = cap
        limit = `${pct(p.min_debt_yield_stabilized)} min debt yield`
      }
    }
    if (p.min_dscr_amortizing != null && constant) {
      const cap = input.noi / (p.min_dscr_amortizing * constant)
      if (cap < loan) {
        loan = cap
        limit = `${p.min_dscr_amortizing.toFixed(2)}x amortizing DSCR at ${pct(coupon ?? 0, 2)} / ${amortYears}-yr`
      }
    }
    if (loan > p.loan_size_max) {
      loan = p.loan_size_max
      limit = `${usd0.format(p.loan_size_max)} program maximum`
    }
    if (loan < p.loan_size_min) {
      return {
        ...base,
        status: 'Out of Box' as const,
        sizedLoan: 0,
        reason: `Sizes to ${usd0.format(loan)}, below the ${usd0.format(p.loan_size_min)} program minimum`,
      }
    }
    return { ...base, status: 'In Box' as const, sizedLoan: loan, reason: `${usd0.format(loan)}, limited by ${limit}` }
  })
}
